const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    CAD: 'CA$',
    AUD: 'A$',
    NZD: 'NZ$',
    SGD: 'S$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    JPY: '¥'
}

const compact = (value: number): string => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`
    if (value >= 1000) return `${Math.round(value / 1000)}k`
    return String(Math.round(value))
}

/** "$150k – $200k" plus the pay period when it is not yearly. */
export const formatSalary = (
    min: number | null,
    max: number | null,
    currency: string | null,
    period: string | null
): string | null => {
    if (!min && !max) return null

    const code = currency ?? 'USD'
    const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `
    const low = min ?? max ?? 0
    const high = max ?? min ?? 0

    const format = (value: number) => (period === 'hour' ? value.toFixed(value % 1 === 0 ? 0 : 2) : compact(value))
    const range = low === high ? `${symbol}${format(low)}` : `${symbol}${format(low)} – ${symbol}${format(high)}`
    const suffix = period === 'hour' ? ' / hr' : period === 'month' ? ' / mo' : ''

    return `${range}${suffix}`
}

const DAY = 86400000

/** Relative age like "Today" or "3 days ago", measured against a fixed `now` so server and browser agree. */
export const relativeAge = (iso: string | null | undefined, now: string): string => {
    if (!iso) return ''

    const then = new Date(iso).getTime()
    const reference = new Date(now).getTime()
    if (!Number.isFinite(then) || !Number.isFinite(reference)) return ''

    const days = Math.floor((reference - then) / DAY)

    if (days <= 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    if (days < 30) {
        const weeks = Math.floor(days / 7)
        return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
    }
    if (days < 365) {
        const months = Math.floor(days / 30)
        return months <= 1 ? '1 month ago' : `${months} months ago`
    }

    return 'Over a year ago'
}

export const isFresh = (iso: string | null | undefined, now: string, hours = 48): boolean => {
    if (!iso) return false
    return new Date(now).getTime() - new Date(iso).getTime() < hours * 3600000
}

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
const MONTH_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })

export const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return ''
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? '' : DATE_FORMAT.format(date)
}

/** Date and time in UTC, labelled so nobody mistakes it for local time. */
export const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return ''
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? '' : `${DATE_TIME_FORMAT.format(date)} UTC`
}

/** "2023-04" becomes "Apr 2023". */
export const formatMonth = (value: string | null | undefined): string => {
    if (!value) return ''
    const [year, month] = value.split('-').map(Number)
    if (!year) return ''
    if (!month) return String(year)
    return MONTH_FORMAT.format(new Date(Date.UTC(year, month - 1, 1)))
}

export const formatNumber = (value: number): string => new Intl.NumberFormat('en-US').format(value)

export const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms} ms`
    const seconds = Math.round(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

export const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
    `${formatNumber(count)} ${count === 1 ? singular : plural}`
